import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import authService from './authService';
import { jwtDecode } from 'jwt-decode';

// Tip tanımlamaları
export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  roles: string[];
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Register için tip tanımlaması
export interface RegisterCredentials {
  username: string;
  email: string;
  password: string;
  role?: 'agency_owner' | 'marketing_specialist' | 'company_owner';
}

// Token'dan rol bilgilerini çıkar
const getRolesFromToken = (token: string): string[] => {
  try {
    const decoded: any = jwtDecode(token);
    return decoded.roles || [];
  } catch (error) {
    console.error('Token decode error:', error);
    return [];
  }
};

// Hata mesajını çıkarma yardımcı fonksiyonu - geliştirilmiş versiyon
const extractErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  
  // FastAPI validation error format
  if (Array.isArray(error)) {
    return error.map(err => err.msg || JSON.stringify(err)).join(', ');
  }
  
  // FastAPI detail error
  if (error?.detail) {
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    // Eğer detail bir array ise
    if (Array.isArray(error.detail)) {
      return error.detail.map((item: any) => 
        item.msg || JSON.stringify(item)
      ).join(', ');
    }
  }
  
  // Standard error message
  if (error?.message) return error.message;
  
  // Validation error with msg field
  if (error?.msg) return error.msg;
  
  // Completely unknown format, stringify safely
  try {
    return JSON.stringify(error) || 'An unexpected error occurred';
  } catch (e) {
    return 'An unexpected error occurred';
  }
};

// Register için async thunk
export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Register error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Registration failed. Please try again.' }
      );
    }
  }
);

// Login için async thunk
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Login error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Login failed. Please try again.' }
      );
    }
  }
);

// Kullanıcı bilgilerini almak için async thunk
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Get user error:', axiosError.response?.data);
      return rejectWithValue(
        axiosError.response?.data || { message: 'Failed to fetch user data.' }
      );
    }
  }
);

// Token'dan geçici kullanıcı bilgilerini oluştur
const getInitialUser = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const roles = getRolesFromToken(token);
      return {
        id: -1, // Geçici ID, getCurrentUser ile güncellenecek
        username: '',
        email: '',
        full_name: '',
        roles: roles
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }
  return null;
};

// Başlangıç durumu
const initialState: AuthState = {
  user: getInitialUser(),
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
};

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        localStorage.setItem('token', action.payload);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<{ access_token: string }>) => {
        state.isLoading = false;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        
        // Token'dan rolleri çıkar ve geçici olarak kullanıcı nesnesine ekle
        const roles = getRolesFromToken(action.payload.access_token);
        if (!state.user) {
          state.user = {
            id: -1, // Geçici ID, getCurrentUser ile güncellenecek
            username: '',
            email: '',
            full_name: '',
            roles: roles
          };
        } else {
          state.user.roles = roles;
        }
        
        localStorage.setItem('token', action.payload.access_token);
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        // Hata mesajını string'e dönüştür
        state.error = extractErrorMessage(action.payload);
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ access_token: string }>) => {
        state.isLoading = false;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        
        // Token'dan rolleri çıkar ve geçici olarak kullanıcı nesnesine ekle
        const roles = getRolesFromToken(action.payload.access_token);
        if (!state.user) {
          state.user = {
            id: -1, // Geçici ID, getCurrentUser ile güncellenecek
            username: '',
            email: '',
            full_name: '',
            roles: roles
          };
        } else {
          state.user.roles = roles;
        }
        
        localStorage.setItem('token', action.payload.access_token);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        // Hata mesajını string'e dönüştür
        state.error = extractErrorMessage(action.payload);
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        // Hata mesajını string'e dönüştür
        state.error = extractErrorMessage(action.payload);
        
        // Eğer 401 hatası alırsak oturumu sonlandır
        if (action.error.message?.includes('401')) {
          state.isAuthenticated = false;
          state.user = null;
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      });
  },
});

export const { logout, clearError, setToken } = authSlice.actions;
export default authSlice.reducer;